import { Container, Divider, IconButton, Stack } from '@mui/material';
import BasketProfile from 'components/Home/BasketProfileContainer';
import BasketDetailContainer from 'components/Home/BasketDetailContainer';
import BasketActivityContainer from 'components/Home/BasketActivityContainer';
import Tab from 'components/Common/Tab';
import EditIcon from '@mui/icons-material/Edit';
import MenuList from 'components/Common/MenuList';

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import routes from 'constants/routes';
import MuiLoader from 'components/Common/Loader';
import { deleteBasket } from 'services/deleteBasket';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActivityDetail, fetchBasketDetail } from 'redux/slices/baskets/basketDetailsSlice';
import { AppDispatch, RootState } from 'redux/store';
import { notify } from 'components/Common/Toast';
import DownloadCSV from 'components/CSV/DownloadCSV';
import DeleteModal from 'components/Common/DeleteModal';
import { selectSelectedParishId } from 'redux/selectors/parishContext';
import { BasketActivityUser, isSystemBasket } from 'types/baskets';
import { getProblemDetail } from 'api/v1Helpers';
import { formatAmount, getDateAndTime } from 'utils/helpers';

// Columns match BasketActivityUser (GET .../contributions) — no donor name/username/status exists
// on that response, so the export shows what's actually there rather than blank columns.
//
// Values are formatted here rather than exported raw: the amounts are integer minor units, so a
// raw `amountCents` of 2500 lands in a column headed "Gift" and reads as $2,500. `DownloadCSV`'s
// own date/time handling can't help either — it only fires for a key whose name contains "date",
// which `createdAt` does not, so the timestamp would export as a raw ISO string. Deliberately no
// key here contains "date", to keep that normalizer off already-formatted values.
const csvHeaders = [
    { label: 'Date', key: 'occurredAt' },
    { label: 'Donor ID', key: 'userId' },
    { label: 'Gift', key: 'gift' },
    { label: 'Refunded', key: 'refunded' },
    { label: 'Net', key: 'net' },
];

const toCsvRow = (row: BasketActivityUser) => ({
    occurredAt: getDateAndTime(row.createdAt),
    userId: row.userId,
    gift: formatAmount(row.amountCents, 'cents'),
    refunded: formatAmount(row.refundedCents, 'cents'),
    net: formatAmount(row.netCents, 'cents'),
});

function BasketDetailActive() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [tab, setTab] = useState('details');
    const [deleteLoading, setDelLoading] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const { basketDetails, activityDetailData, loading } = useSelector((state: RootState) => state?.baskets?.details);
    const selectedParishId = useSelector(selectSelectedParishId);

    const csvRows = useMemo(() => (activityDetailData || []).map(toCsvRow), [activityDetailData]);

    const { id } = useParams();
    const { status } = basketDetails || {};
    const isSuspended = status === 'SUSPEND';
    // The backend deletes DRAFT baskets only (anything else 409s; the General Fund never), so
    // Delete is offered only there — a menu item that always errors is worse than none. SUSPENDED
    // delete is an open backend dependency: docs/tickets/open/2026-09-25-BE-basket-delete-suspended.md
    const canDelete = status === 'PENDING' && !isSystemBasket(basketDetails);

    useEffect(() => {
        if (id) {
            if (tab === 'details') {
                dispatch(fetchBasketDetail(id));
            } else {
                dispatch(fetchActivityDetail(id));
            }
        }
    }, [id, tab, dispatch]);

    const setTabValue = (value: string) => {
        setTab(value);
    };

    // Contribution rows still have no working detail PAGE to link to: `row.id` is a real donation
    // id (see types/baskets.ts BasketActivityUser), but GET /v1/donations/:id is donor-scoped only
    // (404s for anyone but the donor themselves) and the /transaction/:id page this used to point
    // at (`row?.paymentId`, a field that doesn't exist on the real response) is itself a frozen
    // Phase-12 stub that never calls any API — see transactionDetails.ts. disableRowNavigation on
    // the table below stays the honest state for a full detail page. The Refund action added to
    // that table doesn't need one, though: POST /v1/donations/:id/refund resolves the donation's
    // parish itself, so the row's already-known id/amounts are all it needs.
    const navigationHandler = () => {};

    const onDelete = async () => {
        if (!selectedParishId || !id) {
            notify('No selected parish', 'error');
            return;
        }
        try {
            setDelLoading(true);
            await deleteBasket(selectedParishId, id);
            notify('Basket deleted successfully!', 'success');
            navigate(routes.home.main);
        } catch (error) {
            // Never log the raw error here — it's an AxiosError whose .config carries the live
            // Authorization bearer token (see createAsyncThunkWrapper's handleErrorMessage).
            notify(getProblemDetail(error) || 'Failed to delete basket', 'error');
        } finally {
            setDelLoading(false);
        }
    };

    const editBasketNavigationHandel = (basketId: string | undefined) => {
        if (basketId) {
            navigate(routes.home.basketUpdate.replace(':id', String(basketId)));
        }
    };
    // Both menu items can be hidden at once (Details tab on any non-DRAFT basket) — an empty
    // MenuList still renders its "⋮" button, opening to a blank popup. Skip the whole menu then.
    const hasVisibleMenuItems = tab === 'activity' || canDelete;

    return (
        <Stack direction={{ xs: 'column', lg: 'row' }} gap={2} justifyContent="space-evenly">
            <div>
                <Stack width="100%" alignItems="center">
                    <BasketProfile basket={basketDetails} />
                </Stack>
                {/* Withdraw Balance removed — Remove-bucket (no wallet withdraw on /v1). */}
            </div>
            <Container sx={{ backgroundColor: 'white', p: 2 }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 6,
                        flexDirection: 'row',
                        justifyContent: 'space-around',
                    }}
                >
                    <Tab
                        tabs={[
                            { value: 'details', label: 'Details' },
                            { value: 'activity', label: 'Activity' },
                        ]}
                        tabValue={tab}
                        setTabValue={setTabValue}
                    />

                    {!isSuspended && status !== 'CLOSED' && (
                        <IconButton onClick={() => editBasketNavigationHandel(id)} sx={{ marginRight: 1 }}>
                            <EditIcon htmlColor="black" />
                        </IconButton>
                    )}
                    {hasVisibleMenuItems && (
                        <MenuList
                            loading={deleteLoading}
                            mt={2}
                            menuItems={[
                                {
                                    item: 'Create CSV File',
                                    hide: tab !== 'activity',
                                    render: () => (
                                        <DownloadCSV data={csvRows} headers={csvHeaders} fileName="Basket-activity">
                                            Create CSV File
                                        </DownloadCSV>
                                    ),
                                    disabled: !activityDetailData?.length,
                                },
                                {
                                    item: 'Delete Basket',
                                    hide: !canDelete,
                                    onClick: () => setDeleteModal(true),
                                    loading: deleteLoading,
                                },
                            ]}
                        />
                    )}
                </div>
                <Divider />
                <MuiLoader loading={loading} absolute />
                <Container
                    component={
                        tab === 'details'
                            ? () => <BasketDetailContainer basketDetails={basketDetails} />
                            : () => (
                                  <BasketActivityContainer
                                      data={activityDetailData}
                                      navigationHandler={navigationHandler}
                                      onRefunded={() => id && dispatch(fetchActivityDetail(id))}
                                  />
                              )
                    }
                />
            </Container>
            <DeleteModal
                isOpen={deleteModal}
                title="Delete Basket"
                onClose={() => setDeleteModal(false)}
                onDelete={onDelete}
                loading={deleteLoading}
                description="Are you sure you want to delete the selected Basket!"
            />
        </Stack>
    );
}

export default BasketDetailActive;
